import { useState } from 'react';

export interface WMSLayer {
  id: string;
  name: string;
  url: string;
  params: any;
  visible: boolean;
  category?: string;
  order?: number;
}

interface LayerCategoryProps {
  categoryId?: string;
  categoryName: string;
  categoryOrder?: number;
  layers: WMSLayer[];
  onLayerToggle: (layerId: string, visible: boolean) => void;
  onLayerReorder?: (layerId: string, newOrder: number) => void;
  onCategoryReorder?: (categoryId: string, newOrder: number) => void;
  isDraggingCategory?: boolean;
  isDragOverCategory?: boolean;
}

const LayerCategory = ({
  categoryId,
  categoryName,
  layers,
  onLayerToggle,
  onLayerReorder,
  onCategoryReorder,
  isDraggingCategory,
  isDragOverCategory
}: LayerCategoryProps) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [draggedLayer, setDraggedLayer] = useState<string | null>(null);
  const [dragOverLayer, setDragOverLayer] = useState<string | null>(null);

  if (layers.length === 0) {
    return null;
  }

  const handleDragStart = (e: React.DragEvent, layerId: string) => {
    setDraggedLayer(layerId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget.innerHTML);
  };

  const handleDragOver = (e: React.DragEvent, layerId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverLayer(layerId);
  };

  const handleDragLeave = () => {
    setDragOverLayer(null);
  };

  const handleDrop = (e: React.DragEvent, targetLayerId: string) => {
    e.preventDefault();
    setDragOverLayer(null);

    if (!draggedLayer || !onLayerReorder || draggedLayer === targetLayerId) {
      setDraggedLayer(null);
      return;
    }

    // Find the target layer's order
    const targetLayer = layers.find(l => l.id === targetLayerId);
    if (targetLayer && targetLayer.order !== undefined) {
      onLayerReorder(draggedLayer, targetLayer.order);
    }

    setDraggedLayer(null);
  };

  const handleDragEnd = () => {
    setDraggedLayer(null);
    setDragOverLayer(null);
  };

  return (
    <div className={`layer-category ${isDraggingCategory ? 'dragging-category' : ''} ${isDragOverCategory ? 'drag-over-category' : ''}`}>
      <div className="category-header" onClick={() => setIsExpanded(!isExpanded)}>
        {onCategoryReorder && categoryId && (
          <span className="category-drag-handle" title="Drag to reorder category">
            ☰
          </span>
        )}
        <span className="category-title">{categoryName}</span>
        <span className={`category-arrow ${isExpanded ? 'expanded' : ''}`}>
          ▼
        </span>
      </div>

      {isExpanded && (
        <div className="category-layers">
          {layers.map((layer, index) => (
            <div
              key={layer.id}
              className={`layer-item ${draggedLayer === layer.id ? 'dragging' : ''} ${dragOverLayer === layer.id ? 'drag-over' : ''}`}
              draggable={!!onLayerReorder}
              onDragStart={(e) => handleDragStart(e, layer.id)}
              onDragOver={(e) => handleDragOver(e, layer.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, layer.id)}
              onDragEnd={handleDragEnd}
            >
              {onLayerReorder && (
                <span className="drag-handle" title="Drag to reorder">
                  ☰
                </span>
              )}
              <input
                type="checkbox"
                id={`layer-${layer.id}`}
                className="layer-checkbox"
                checked={layer.visible}
                onChange={(e) => onLayerToggle(layer.id, e.target.checked)}
              />
              <label htmlFor={`layer-${layer.id}`} className="layer-label">
                {layer.name}
              </label>
              {onLayerReorder && (
                <span className="layer-order-badge" title="Layer order (higher = on top)">
                  {layers.length - index}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LayerCategory;
